import React, { useState } from 'react';
import { Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { putData, deleteData, postDataWithAuth } from '../../../helpers/ApiSettings';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../helpers/user';

const SecuritySettings = ({ userData }) => {
  const navigate = useNavigate();
  const userToken = JSON.parse(localStorage.getItem('ovlm_user'))?.jwt;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccessMessage('');
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const errors = [];
    if (password.length < minLength) errors.push(`Au moins ${minLength} caractères`);
    if (!hasUpperCase) errors.push('Une majuscule');
    if (!hasLowerCase) errors.push('Une minuscule');
    if (!hasNumbers) errors.push('Un chiffre');
    if (!hasSpecialChar) errors.push('Un caractère spécial');

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    const passwordErrors = validatePassword(formData.newPassword);
    if (passwordErrors.length > 0) {
      setError(`Le nouveau mot de passe doit contenir : ${passwordErrors.join(', ')}`);
      return;
    }

    setLoading(true);

    try {
      if (!userToken) {
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }

      const response = await postDataWithAuth('/auth/change-password', {
        currentPassword: formData.currentPassword,
        password: formData.newPassword,
        passwordConfirmation: formData.confirmPassword
      }, userToken);

      if (response.error) {
        throw new Error(response.error.message || 'Erreur lors du changement de mot de passe');
      }

      setSuccessMessage('Mot de passe modifié avec succès');
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Password change error:', error);
      
      if (error.response?.data?.error?.message) {
        setError(error.response.data.error.message);
      } else if (error.message.includes('Current password')) {
        setError('Mot de passe actuel incorrect');
      } else {
        setError(error.message || 'Une erreur est survenue');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== userData.email) {
      setError("L'adresse email ne correspond pas");
      return;
    }

    setLoading(true);
    try {
      await deleteData(`/users/${userData.id}`);
      await logout();
      navigate('/');
    } catch (error) {
      setError('Erreur lors de la suppression du compte');
      setLoading(false);
    }
  };

  return (
    <>
      <div className="card">
        <div className="card-body">
          <h3 className="card-title mb-4">
            <Lock className="me-2" />
            Paramètres de sécurité
          </h3>

          {successMessage && (
            <div className="alert alert-success" role="alert">
              {successMessage}
            </div>
          )}

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Mot de passe actuel</label>
              <div className="input-group">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  className="form-control"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Nouveau mot de passe</label>
              <div className="input-group">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  className="form-control"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label">Confirmer le nouveau mot de passe</label>
              <div className="input-group">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="form-control"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="mb-3">
              <p className="text-muted small">
                Le mot de passe doit contenir au moins :
                <ul className="mb-0">
                  <li>8 caractères</li>
                  <li>Une majuscule</li>
                  <li>Une minuscule</li>
                  <li>Un chiffre</li>
                  <li>Un caractère spécial (!@#$%^&*)</li>
                </ul>
              </p>
            </div>

            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Modification en cours...
                </>
              ) : (
                'Modifier le mot de passe'
              )}
            </button>
          </form>

          <hr className="my-4" />

          <div className="delete-account-section">
            <h4 className="text-danger mb-3">Supprimer le compte</h4>
            <p className="text-muted">
              Cette action est irréversible. Toutes vos données seront supprimées définitivement.
            </p>
            <button 
              className="btn btn-outline-danger"
              onClick={() => setShowDeleteModal(true)}
              disabled={loading}
            >
              Supprimer mon compte
            </button>
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div 
          className="modal fade show" 
          style={{ 
            display: 'block', 
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1050,
            overflowX: 'hidden',
            overflowY: 'auto',
          }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <AlertTriangle className="me-2" />
                  Supprimer le compte
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmation('');
                    setError('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <p className="mb-0">
                    <strong>Attention :</strong> Cette action est irréversible. Toutes vos données seront supprimées définitivement.
                  </p>
                </div>

                <p>Pour confirmer la suppression, veuillez saisir votre adresse email : <strong>{userData.email}</strong></p>
                
                <input
                  type="email"
                  className="form-control"
                  placeholder="Entrez votre email"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmation('');
                    setError('');
                  }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteAccount}
                  disabled={loading || deleteConfirmation !== userData.email}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Suppression...
                    </>
                  ) : (
                    'Supprimer définitivement'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SecuritySettings;